import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Download, Check, X, Loader2, CalendarDays, BarChart3 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December'
];

export default function Attendance() {
  const [activeTab, setActiveTab] = useState('daily');

  // Daily state
  const [selectedDate, setSelectedDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [drivers, setDrivers] = useState([]);
  const [attendance, setAttendance] = useState({});
  const [loadingDrivers, setLoadingDrivers] = useState(true);
  const [saving, setSaving] = useState(false);

  // Report state
  const now = new Date();
  const [reportMonth, setReportMonth] = useState(now.getMonth() + 1);
  const [reportYear, setReportYear] = useState(now.getFullYear());
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    loadDrivers();
  }, []);

  useEffect(() => {
    if (drivers.length > 0) loadAttendance(selectedDate);
  }, [selectedDate, drivers]);

  const loadDrivers = async () => {
    try {
      const res = await api.get('/drivers');
      const active = res.data.filter(d => d.status === 'active');
      setDrivers(active);
    } catch (err) {
      console.error('Failed to load drivers', err);
    } finally {
      setLoadingDrivers(false);
    }
  };

  const loadAttendance = async (date) => {
    try {
      const res = await api.get(`/attendance?date=${date}`);
      const map = {};
      res.data.forEach(r => { map[r.driver_id] = r.status; });
      setAttendance(map);
    } catch (err) {
      console.error('Failed to load attendance', err);
    }
  };

  const toggleStatus = (driverId) => {
    setAttendance(prev => {
      const current = prev[driverId];
      if (!current || current === 'absent') return { ...prev, [driverId]: 'present' };
      return { ...prev, [driverId]: 'absent' };
    });
  };

  const markAll = (status) => {
    const map = {};
    drivers.forEach(d => { map[d.driver_id] = status; });
    setAttendance(map);
  };

  const saveAttendance = async () => {
    const records = drivers.map(d => ({
      driver_id: d.driver_id,
      date: selectedDate,
      status: attendance[d.driver_id] || 'absent',
    }));

    setSaving(true);
    try {
      await api.post('/attendance/mark', { date: selectedDate, records });
      toast.success(`Attendance saved for ${selectedDate}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail) toast.error(detail);
      else toast.error('Failed to save attendance');
    } finally {
      setSaving(false);
    }
  };

  const loadReport = async () => {
    setLoadingReport(true);
    try {
      const res = await api.get(`/attendance/monthly-report?month=${reportMonth}&year=${reportYear}`);
      setReport(res.data);
    } catch (err) {
      toast.error('Failed to load report');
    } finally {
      setLoadingReport(false);
    }
  };

  const exportCSV = async () => {
    setExporting(true);
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/attendance/export-csv?month=${reportMonth}&year=${reportYear}`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_${reportYear}_${String(reportMonth).padStart(2,'0')}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported');
    } catch (err) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const presentCount = Object.values(attendance).filter(s => s === 'present').length;
  const absentCount = Object.values(attendance).filter(s => s === 'absent').length;

  return (
    <div data-testid="attendance-page">
      <div className="mb-8">
        <h1 className="text-4xl font-black font-heading text-gray-900 mb-2">Attendance</h1>
        <p className="text-gray-600">Mark daily attendance and view monthly reports</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit" data-testid="attendance-tabs">
        <button
          onClick={() => setActiveTab('daily')}
          data-testid="tab-daily"
          className={`px-5 py-2.5 rounded-md text-sm font-semibold transition-all flex items-center gap-2 ${
            activeTab === 'daily' ? 'bg-white text-shizah-blue shadow-sm' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <CalendarDays size={16} /> Daily Marking
        </button>
        <button
          onClick={() => setActiveTab('report')}
          data-testid="tab-report"
          className={`px-5 py-2.5 rounded-md text-sm font-semibold transition-all flex items-center gap-2 ${
            activeTab === 'report' ? 'bg-white text-shizah-blue shadow-sm' : 'text-gray-600 hover:text-gray-900'
          }`}
        >
          <BarChart3 size={16} /> Monthly Report
        </button>
      </div>

      {/* ──── Daily Marking Tab ──── */}
      {activeTab === 'daily' && (
        <div className="space-y-6">
          {/* Date Picker & Actions */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1 block">Date</label>
                <Input
                  type="date"
                  value={selectedDate}
                  onChange={e => setSelectedDate(e.target.value)}
                  data-testid="date-picker"
                  className="w-48"
                />
              </div>
              <Button variant="outline" onClick={() => markAll('present')} data-testid="mark-all-present" className="border-green-500 text-green-700 hover:bg-green-50">
                <Check size={16} className="mr-1" /> Mark All Present
              </Button>
              <Button variant="outline" onClick={() => markAll('absent')} data-testid="mark-all-absent" className="border-red-500 text-red-700 hover:bg-red-50">
                <X size={16} className="mr-1" /> Mark All Absent
              </Button>
              <div className="ml-auto flex items-center gap-4">
                <span className="text-sm"><span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-1" /> Present: <strong>{presentCount}</strong></span>
                <span className="text-sm"><span className="inline-block w-3 h-3 rounded-full bg-red-500 mr-1" /> Absent: <strong>{absentCount}</strong></span>
              </div>
            </div>
          </div>

          {/* Driver Attendance Grid */}
          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            {loadingDrivers ? (
              <div className="flex items-center justify-center h-32">
                <Loader2 className="animate-spin" size={24} />
              </div>
            ) : drivers.length === 0 ? (
              <div className="p-8 text-center text-gray-500">No active drivers found. Add drivers first.</div>
            ) : (
              <>
                <table className="w-full" data-testid="attendance-table">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Driver ID</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Mobile</th>
                      <th className="px-6 py-3 text-center text-xs font-bold text-gray-700 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {drivers.map(d => {
                      const st = attendance[d.driver_id];
                      const isPresent = st === 'present';
                      const isAbsent = st === 'absent';
                      return (
                        <tr key={d.driver_id} className="hover:bg-gray-50" data-testid={`att-row-${d.driver_id}`}>
                          <td className="px-6 py-3 text-sm font-semibold text-shizah-blue">{d.driver_id}</td>
                          <td className="px-6 py-3 text-sm text-gray-900">{d.full_name}</td>
                          <td className="px-6 py-3 text-sm text-gray-600">{d.mobile}</td>
                          <td className="px-6 py-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => setAttendance(prev => ({ ...prev, [d.driver_id]: 'present' }))}
                                data-testid={`mark-present-${d.driver_id}`}
                                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                                  isPresent
                                    ? 'bg-green-600 text-white shadow-sm'
                                    : 'bg-gray-100 text-gray-500 hover:bg-green-50 hover:text-green-700'
                                }`}
                              >
                                <Check size={14} className="inline mr-1" />Present
                              </button>
                              <button
                                onClick={() => setAttendance(prev => ({ ...prev, [d.driver_id]: 'absent' }))}
                                data-testid={`mark-absent-${d.driver_id}`}
                                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
                                  isAbsent
                                    ? 'bg-red-600 text-white shadow-sm'
                                    : 'bg-gray-100 text-gray-500 hover:bg-red-50 hover:text-red-700'
                                }`}
                              >
                                <X size={14} className="inline mr-1" />Absent
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                <div className="p-4 border-t border-gray-200 flex justify-end">
                  <Button
                    onClick={saveAttendance}
                    disabled={saving}
                    data-testid="save-attendance-button"
                    className="bg-shizah-blue hover:bg-blue-700 text-white px-8"
                  >
                    {saving ? <><Loader2 className="animate-spin mr-2" size={18} />Saving...</> : 'Save Attendance'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ──── Monthly Report Tab ──── */}
      {activeTab === 'report' && (
        <div className="space-y-6">
          {/* Filters */}
          <div className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1 block">Month</label>
                <Select value={String(reportMonth)} onValueChange={v => setReportMonth(Number(v))}>
                  <SelectTrigger className="w-44" data-testid="report-month-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {MONTHS.map((m, i) => (
                      <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-1 block">Year</label>
                <Select value={String(reportYear)} onValueChange={v => setReportYear(Number(v))}>
                  <SelectTrigger className="w-32" data-testid="report-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map(y => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={loadReport} disabled={loadingReport} data-testid="load-report-button" className="bg-shizah-blue hover:bg-blue-700 text-white">
                {loadingReport ? <><Loader2 className="animate-spin mr-2" size={16} />Loading...</> : 'View Report'}
              </Button>
              {report && (
                <Button
                  onClick={exportCSV}
                  disabled={exporting}
                  variant="outline"
                  data-testid="export-attendance-csv"
                  className="ml-auto border-shizah-blue text-shizah-blue hover:bg-blue-50"
                >
                  <Download size={16} className="mr-2" />
                  {exporting ? 'Exporting...' : 'Export CSV'}
                </Button>
              )}
            </div>
          </div>

          {/* Report Table */}
          {report && (
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200 bg-gray-50 flex items-center justify-between">
                <h3 className="font-bold text-gray-900">
                  {MONTHS[report.month - 1]} {report.year} — Attendance Report
                </h3>
                <span className="text-sm text-gray-600">Total marked days: {report.total_working_days}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="report-table">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Driver ID</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 uppercase">Mobile</th>
                      <th className="px-6 py-3 text-center text-xs font-bold text-gray-700 uppercase">Present Days</th>
                      <th className="px-6 py-3 text-center text-xs font-bold text-gray-700 uppercase">Absent Days</th>
                      <th className="px-6 py-3 text-center text-xs font-bold text-gray-700 uppercase">Total Marked</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {report.drivers.length === 0 ? (
                      <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No data for this month.</td></tr>
                    ) : (
                      report.drivers.map(d => (
                        <tr key={d.driver_id} className="hover:bg-gray-50" data-testid={`report-row-${d.driver_id}`}>
                          <td className="px-6 py-3 text-sm font-semibold text-shizah-blue">{d.driver_id}</td>
                          <td className="px-6 py-3 text-sm text-gray-900">{d.full_name}</td>
                          <td className="px-6 py-3 text-sm text-gray-600">{d.mobile}</td>
                          <td className="px-6 py-3 text-center">
                            <span className="inline-block bg-green-100 text-green-800 font-bold rounded-full px-3 py-1 text-sm">{d.present_days}</span>
                          </td>
                          <td className="px-6 py-3 text-center">
                            <span className="inline-block bg-red-100 text-red-800 font-bold rounded-full px-3 py-1 text-sm">{d.absent_days}</span>
                          </td>
                          <td className="px-6 py-3 text-center text-sm font-semibold text-gray-700">{d.total_marked}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
